/**
 * Project store.
 *
 * Owns the active workspace's project list for `ProjectsPage`; link
 * management for a single project's grouped entities lives in
 * `ProjectDetailPage`, calling `projectRepository` directly for the
 * detail-only `listLinks`/`addLink`/`removeLink` operations rather than
 * holding every project's links in this store's state.
 */
import { create } from "zustand";
import type { Project } from "@/types";
import { projectRepository } from "@/services/storage/projectRepository";
import { logger } from "@/services/logger";
import { isTauri } from "@/utils";
import { useAppStore } from "./appStore";
import { useSettingsStore } from "./settingsStore";

const log = logger.scope("project-store");

interface ProjectState {
  projects: Project[];
  loaded: boolean;

  init: () => Promise<void>;
  refresh: () => Promise<void>;
  create: (name: string, description?: string) => Promise<Project | null>;
  update: (id: string, patch: { name?: string; description?: string; tags?: string[] }) => Promise<void>;
  setArchived: (id: string, archived: boolean) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

async function guarded(action: () => Promise<void>, failureTitle: string): Promise<void> {
  try {
    await action();
  } catch (err) {
    log.error(failureTitle, err);
    useAppStore.getState().addToast({
      title: failureTitle,
      description: err instanceof Error ? err.message : undefined,
      variant: "error",
    });
  }
}

export const useProjectStore = create<ProjectState>()((set, get) => ({
  projects: [],
  loaded: false,

  init: async () => {
    if (get().loaded || !isTauri()) {
      set({ loaded: true });
      return;
    }
    await get().refresh();
  },

  refresh: async () => {
    const workspaceId = useSettingsStore.getState().activeWorkspaceId;
    try {
      const projects = await projectRepository.list(workspaceId);
      set({ projects, loaded: true });
    } catch (err) {
      log.error("Failed to load projects", err);
      set({ loaded: true });
    }
  },

  create: async (name, description) => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    const workspaceId = useSettingsStore.getState().activeWorkspaceId;
    let created: Project | null = null;
    await guarded(async () => {
      created = await projectRepository.create({ workspaceId, name: trimmed, description });
      await get().refresh();
    }, "Couldn't create project");
    return created;
  },

  update: async (id, patch) => {
    await guarded(async () => {
      await projectRepository.update(id, patch);
      await get().refresh();
    }, "Couldn't update project");
  },

  setArchived: async (id, archived) => {
    await guarded(async () => {
      await projectRepository.setArchived(id, archived);
      await get().refresh();
    }, "Couldn't update project");
  },

  remove: async (id) => {
    set((s) => ({ projects: s.projects.filter((p) => p.id !== id) }));
    await guarded(() => projectRepository.delete(id), "Couldn't delete project");
  },
}));
